import {
  AnyApiFactory,
  ApiRef,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  storageApiRef,
  identityApiRef,
  fetchApiRef,
  gitlabAuthApiRef,
  googleAuthApiRef,
  microsoftAuthApiRef,
  oktaAuthApiRef,
  oneloginAuthApiRef,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  visitsApiRef,
  VisitsStorageApi,
} from '@backstage/plugin-home';
import {
  kubernetesApiRef,
  kubernetesAuthProvidersApiRef,
  kubernetesProxyApiRef,
  kubernetesClusterLinkFormatterApiRef,
  KubernetesBackendClient,
  KubernetesAuthProviders,
  KubernetesProxyClient,
  KubernetesClusterLinkFormatter,
  getDefaultFormatters,
  DEFAULT_FORMATTER_NAME,
} from '@backstage/plugin-kubernetes-react';

// OIDC API Reference for Keycloak - 这是我们唯一需要自定义的 API
export const oidcAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.oidc',
});

// 按照 Backstage 标准扩展方式：核心 API + 必要的自定义 API
export const apis: AnyApiFactory[] = [
  // SCM 集成 API - Catalog 核心功能必需
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  
  // Home 插件访问统计 API - 按照官方文档标准配置
  createApiFactory({
    api: visitsApiRef,
    deps: {
      storageApi: storageApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ storageApi, identityApi }) =>
      VisitsStorageApi.create({ storageApi, identityApi }),
  }),
  
  // OIDC API Factory for Keycloak - 自定义认证提供者
    createApiFactory({
      api: oidcAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          provider: {
            id: 'oidc',
            title: 'Keycloak',
            icon: () => null,
          },
          environment: configApi.getOptionalString('auth.environment'),
          defaultScopes: ['openid', 'profile', 'email'],
        }),
    }),
  
  // Kubernetes API - 手动注册以确保其他组件可以使用
  createApiFactory({
    api: kubernetesAuthProvidersApiRef,
    deps: {
      gitlabAuthApi: gitlabAuthApiRef,
      googleAuthApi: googleAuthApiRef,
      microsoftAuthApi: microsoftAuthApiRef,
      oktaAuthApi: oktaAuthApiRef,
      oneloginAuthApi: oneloginAuthApiRef,
    },
    factory: ({
      gitlabAuthApi,
      googleAuthApi,
      microsoftAuthApi,
      oktaAuthApi,
      oneloginAuthApi,
    }) => {
      const oidcProviders = {
        gitlab: gitlabAuthApi,
        google: googleAuthApi,
        microsoft: microsoftAuthApi,
        okta: oktaAuthApi,
        onelogin: oneloginAuthApi,
      };
      return new KubernetesAuthProviders({ oidcProviders });
    },
  }),
  createApiFactory({
    api: kubernetesApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
      kubernetesAuthProvidersApi: kubernetesAuthProvidersApiRef,
    },
    factory: ({ discoveryApi, fetchApi, kubernetesAuthProvidersApi }) =>
      new KubernetesBackendClient({
        discoveryApi,
        fetchApi,
        kubernetesAuthProvidersApi,
      }),
  }),
  createApiFactory({
    api: kubernetesProxyApiRef,
    deps: {
      kubernetesApi: kubernetesApiRef,
    },
    factory: ({ kubernetesApi }) =>
      new KubernetesProxyClient({ kubernetesApi }),
  }),
  createApiFactory({
    api: kubernetesClusterLinkFormatterApiRef,
    deps: {},
    factory: () =>
      new KubernetesClusterLinkFormatter({
        formatters: getDefaultFormatters(),
        defaultFormatterName: DEFAULT_FORMATTER_NAME,
        }),
    }),
];
